import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Table, Button, Modal, Form, Input, Select, Space, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, UserOutlined, TeamOutlined } from '@ant-design/icons';
import BASEURL from '../API/Config';

const { Option } = Select;

const User = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [search, setSearch] = useState('');
  const [form] = Form.useForm();
  const token = localStorage.getItem('token');

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASEURL}/users/list`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setUsers(data);
    } catch { Swal.fire('Error', 'Failed to load users.', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const openModal = (user = null) => {
    if (user) {
      setEditing(true);
      setEditId(user.id);
      form.setFieldsValue({ name: user.name, type: user.type, email: user.email });
    } else {
      setEditing(false);
      setEditId(null);
      form.resetFields();
      setPhoto(null);
    }
    setShowModal(true);
  };

  const handleSubmit = async (values) => {
    const formData = new FormData();
    formData.append('name', values.name);
    formData.append('type', values.type);
    formData.append('email', values.email);
    if (values.password) formData.append('password', values.password);
    if (photo) formData.append('photo', photo);
    try {
      const url = editing ? `${BASEURL}/users/${editId}` : `${BASEURL}/users/create`;
      const res = await fetch(url, { method: editing ? 'PUT' : 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData });
      if (!res.ok) throw new Error();
      Swal.fire('Success', editing ? 'User updated!' : 'User created!', 'success');
      setShowModal(false);
      fetchUsers();
    } catch { Swal.fire('Error', 'Failed to save user.', 'error'); }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({ title: 'Delete User?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#e53935', confirmButtonText: 'Yes, Delete' });
    if (!result.isConfirmed) return;
    await fetch(`${BASEURL}/users/delete/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    Swal.fire('Deleted!', '', 'success'); fetchUsers();
  };

  const typeColorMap = { vendor: 'blue', deliveryboy: 'green', admin: 'red' };
  const filtered = users.filter(u => (u.name || '').toLowerCase().includes(search.toLowerCase()) || (u.email || '').toLowerCase().includes(search.toLowerCase()));

  const columns = [
    { title: "S.No", key: "idx", width: 60, render: (_, __, i) => <span style={{ color: "#6b7280" }}>{i + 1}</span> },
    {
      title: 'USER', key: 'user',
      render: (r) => (
        <Space>
          {r.photo ? (
            <img src={r.photo} alt={r.name} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
          ) : (
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#e8f1ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2874f0', fontWeight: 700 }}>
              {(r.name || '?')[0].toUpperCase()}
            </div>
          )}
          <div>
            <div style={{ fontWeight: 600, color: '#212121', fontSize: 13 }}>{r.name}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>{r.email}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'TYPE', key: 'type',
      render: (r) => <Tag color={typeColorMap[r.type] || 'default'} style={{ borderRadius: 50, fontWeight: 600, textTransform: 'capitalize' }}>{r.type}</Tag>,
    },
    {
      title: 'ACTIONS', key: 'actions', width: 110,
      render: (r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openModal(r)}
            style={{ color: '#2874f0', borderColor: '#2874f0', borderRadius: 4 }} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r.id)}
            style={{ borderRadius: 4 }} />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>Administration</div>
          <h2 style={{ margin: 0, fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 700, color: '#212121' }}>User Management</h2>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}
          style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, height: 38, fontWeight: 600 }}>
          Create User
        </Button>
      </div>

      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8ecf0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Input allowClear prefix={<SearchOutlined style={{ color: '#9ca3af' }} />} placeholder="Search name, email..."
            value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 260, borderRadius: 4 }} />
          <div style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>
            Total: <strong style={{ color: '#212121' }}>{filtered.length}</strong>
          </div>
        </div>
        <Table columns={columns} dataSource={filtered} rowKey="id" loading={loading}
          pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "30", "40"] }} size="middle" />
      </div>

      <Modal
        title={<span style={{ fontFamily: 'Outfit,sans-serif', fontWeight: 700 }}>{editing ? 'Edit User' : 'Create User'}</span>}
        open={showModal} onCancel={() => setShowModal(false)} footer={null} centered width={480}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Full Name" rules={[{ required: true }]}>
            <Input prefix={<UserOutlined />} placeholder="Enter name" size="large" />
          </Form.Item>
          <Form.Item name="type" label="User Type" rules={[{ required: true }]}>
            <Select placeholder="Select type" size="large">
              <Option value="vendor">Vendor</Option>
              <Option value="deliveryboy">Delivery Boy</Option>
              <Option value="admin">Admin</Option>
            </Select>
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input placeholder="Enter email" size="large" />
          </Form.Item>
          <Form.Item name="password" label={editing ? 'New Password (optional)' : 'Password'} rules={editing ? [] : [{ required: true }]}>
            <Input.Password placeholder="Enter password" size="large" />
          </Form.Item>
          <Form.Item label="Profile Photo">
            <input type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files[0])}
              style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, width: '100%' }} />
          </Form.Item>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit"
              style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, fontWeight: 600 }}>
              {editing ? 'Update User' : 'Create User'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default User;


